import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartNoAxesCombined } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import MarkdownRenderer from '../common/MarkdownRenderer';
import AnalysisTypeBadge from './AnalysisTypeBadge';
import { DATE_TIME_FORMAT, formatDateSafe } from '../../utils/dateUtils';
import type { AnalysisEntry } from '@/types/documents';

interface AnalysisHistoryViewProps {
  analysisHistory?: AnalysisEntry[];
}

const AnalysisHistoryView: React.FC<AnalysisHistoryViewProps> = ({ analysisHistory = [] }) => {
  const { t, i18n } = useTranslation();

  const sorted = useMemo(
    () => [...analysisHistory].sort((a, b) => b.version - a.version),
    [analysisHistory],
  );

  if (sorted.length === 0) {
    return (
      <Alert variant="info">
        <ChartNoAxesCombined className="size-4" strokeWidth={1.9} />
        <AlertTitle className="revy-cjk leading-[1.5]">
          {t('documents.analysisHistoryView.emptyTitle')}
        </AlertTitle>
        <AlertDescription className="revy-cjk leading-[1.6] text-muted-foreground">
          {t('documents.analysisHistoryView.emptyDescription')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <section>
      <h2 className="revy-cjk mb-6 text-base font-medium leading-[1.5]">
        {t('documents.analysisHistoryView.title', { count: analysisHistory.length })}
      </h2>

      <ul className="flex flex-col gap-4">
        {sorted.map((entry) => (
          <li key={entry.id} className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="border-b bg-muted/50 px-4 py-3">
              <div className="mb-1 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <AnalysisTypeBadge type={entry.type} />
                  <span className="revy-figure text-xs text-muted-foreground">v{entry.version}</span>
                </div>
                <span className="revy-figure text-xs text-muted-foreground">
                  {formatDateSafe(entry.created_at, DATE_TIME_FORMAT, { t, language: i18n.language })}
                </span>
              </div>
              <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
                {t('documents.analysisHistoryView.createdBy', { name: entry.created_by })}
              </p>
            </div>

            {/* Independent items, matching MUI's accordions: opening one entry
                never collapsed another. */}
            <Accordion
              type="multiple"
              defaultValue={entry.id === sorted[0].id ? [entry.id] : []}
            >
              <AccordionItem value={entry.id} className="border-b-0">
                <AccordionTrigger className="min-h-12 px-4 py-0 text-sm font-medium hover:no-underline data-[state=open]:min-h-12">
                  <span className="revy-cjk">
                    {t('documents.analysisHistoryView.showContent')}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-6 pt-0">
                  <MarkdownRenderer
                    content={entry.content}
                    variant="body2"
                    className="[&_h6]:text-primary"
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default AnalysisHistoryView;
